import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Upload, ClipboardPaste, Inbox, ExternalLink, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";

const KIND_TONE = {
  quote: "bg-sky-100 text-sky-800", ack: "bg-indigo-100 text-indigo-800",
  ship: "bg-teal-100 text-teal-800", unknown: "bg-slate-100 text-slate-600",
};
const STATUS_TONE = {
  filed: "bg-emerald-100 text-emerald-800", needs_review: "bg-amber-100 text-amber-800",
  dismissed: "bg-slate-100 text-slate-500", new: "bg-slate-100 text-slate-600",
};

/** purchasing@absinc.com — what the vendors sent back, sorted into quotes, acknowledgements and
 *  ship notices, each already written onto its PO. What could not be matched waits here. */
export default function InboxSection({ project, purchaseOrders, canEdit, onChanged, onOpenPo }) {
  const [data, setData] = useState(null);
  const [paste, setPaste] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { setData(await opsApi.purchasingInbox({ project_id: project.id })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load the inbox"); }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const res = await opsApi.purchasingInboxUpload(Array.from(files), project.id);
      res.items.forEach((i) => (i.status === "filed" ? toast.success(`${i.kind_label}: ${i.summary || "filed"}`)
        : toast.warning(`${i.kind_label}: ${i.why || "needs review"}`)));
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be read"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That did not work"); }
    finally { setBusy(false); }
  };

  const livePos = (purchaseOrders || []).filter((p) => p.status !== "void");

  return (
    <div className="space-y-3" data-testid="ops-po-inbox">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs text-slate-600">
        <Inbox className="h-3.5 w-3.5 text-slate-400" />
        <span>Vendor mail to <b>{data?.mailbox || "purchasing@absinc.com"}</b> lands here: quotes go to the queue, acknowledgements and
          ship notices are written onto the PO and filed under Documents › Procurement.</span>
        {canEdit && (
          <span className="ml-auto flex items-center gap-1.5">
            <input ref={fileRef} type="file" multiple accept=".eml,.pdf,.docx,.txt,.csv" className="hidden"
                   data-testid="ops-po-inbox-file" onChange={(e) => upload(e.target.files)} />
            <Button variant="data" size="sm" className="h-7 text-xs" onClick={() => setPaste(true)} title="Paste an email" data-testid="ops-po-inbox-paste">
              <ClipboardPaste className="h-3.5 w-3.5" />
            </Button>
            <Button variant="data" size="sm" className="h-7 text-xs" onClick={() => fileRef.current?.click()} disabled={busy} title="Upload .eml or document" data-testid="ops-po-inbox-upload">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            </Button>
          </span>
        )}
      </div>

      {!data ? (
        <p className="flex items-center gap-2 px-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
      ) : data.items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500" data-testid="ops-po-inbox-empty">
          Nothing from the vendors yet.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white" data-testid="ops-po-inbox-list">
          {data.items.map((i) => (
            <li key={i.id} className={`px-4 py-2.5 text-xs ${i.status === "dismissed" ? "opacity-50" : ""}`} data-testid={`ops-po-inbox-item-${i.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 font-medium ${KIND_TONE[i.kind] || ""}`} data-testid={`ops-po-inbox-kind-${i.id}`}>{i.kind_label}</span>
                <span className="font-semibold text-slate-900 truncate max-w-[40%]" title={i.subject}>{i.subject || "(no subject)"}</span>
                <span className="text-slate-500 truncate max-w-[20%]" title={i.from}>{i.from_name || i.from_email || i.from}</span>
                {i.po_number && (
                  <button className="font-mono text-[#007BFF] hover:underline" onClick={() => onOpenPo(i.po_id)} data-testid={`ops-po-inbox-po-${i.id}`}>
                    {i.po_number} <ExternalLink className="inline h-3 w-3" />
                  </button>
                )}
                <span className={`rounded-full px-2 py-0.5 ${STATUS_TONE[i.status] || ""}`} data-testid={`ops-po-inbox-status-${i.id}`}>
                  {i.status === "needs_review" ? "needs review" : i.status}
                </span>
                <span className="ml-auto text-slate-400">{(i.received_at || "").slice(0, 10)} · {i.channel}</span>
              </div>
              {(i.summary || i.why) && (
                <p className={`mt-1 ${i.why ? "text-amber-800" : "text-slate-600"}`}>{i.summary}{i.summary && i.why ? " · " : ""}{i.why}</p>
              )}
              {i.discrepancies?.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-rose-700">{i.discrepancies.map((d, k) => <li key={k}>{d.message}</li>)}</ul>
              )}
              {canEdit && i.status === "needs_review" && (
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <select className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs" defaultValue=""
                          data-testid={`ops-po-inbox-assign-${i.id}`}
                          onChange={(e) => e.target.value && act(() => opsApi.purchasingInboxAssign(i.id, { po_id: e.target.value }), "Re-filed against the PO")}>
                    <option value="">Point at a PO…</option>
                    {livePos.map((p) => <option key={p.id} value={p.id}>{p.po_number} · {p.manufacturer} · {p.status_label}</option>)}
                  </select>
                  <select className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs" value={i.kind}
                          data-testid={`ops-po-inbox-kind-select-${i.id}`}
                          onChange={(e) => i.po_id && act(() => opsApi.purchasingInboxAssign(i.id, { po_id: i.po_id, kind: e.target.value }), "Re-read as " + e.target.value)}>
                    <option value="quote">Vendor quote</option><option value="ack">Acknowledgement</option>
                    <option value="ship">Ship notice</option><option value="unknown">Unclassified</option>
                  </select>
                  <button className="inline-flex items-center gap-1 text-slate-500 hover:text-rose-700" data-testid={`ops-po-inbox-dismiss-${i.id}`}
                          onClick={() => act(() => opsApi.purchasingInboxDismiss(i.id), "Dismissed")}>
                    <X className="h-3 w-3" /> Dismiss
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {paste && <PasteDialog project={project} onClose={() => setPaste(false)} onDone={() => { setPaste(false); load(); onChanged?.(); }} />}
    </div>
  );
}

function PasteDialog({ project, onClose, onDone }) {
  const [text, setText] = useState("");
  const [from, setFrom] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!text.trim()) { toast.error("Paste the email"); return; }
    setBusy(true);
    try {
      const subject = (text.match(/^\s*subject\s*:\s*(.+)$/im) || [])[1] || text.split("\n")[0].slice(0, 120);
      const fromLine = from || (text.match(/^\s*from\s*:\s*(.+)$/im) || [])[1] || "";
      const email = (fromLine.match(/[\w.+-]+@[\w.-]+/) || [])[0] || "";
      const res = await opsApi.purchasingInboxManual({ subject, body: text, from_name: fromLine.replace(/<.*?>/, "").trim(),
        from_email: email, project_id: project.id });
      (res.status === "filed" ? toast.success : toast.warning)(`${res.kind_label}: ${res.summary || res.why || res.status}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be read"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-xl rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="ops-po-inbox-paste-dialog">
        <h3 className="text-base font-semibold text-slate-900">Paste a vendor email</h3>
        <p className="mt-1 text-xs text-slate-500">Whole email — From / Subject lines are read if present. Quote, acknowledgement or ship notice is worked out from the wording and the PO number.</p>
        <Input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="From (optional) — e.g. Bobrick Orders <orders@bobrick.com>" className="mt-3 h-8 text-xs" data-testid="ops-po-inbox-paste-from" />
        <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} className="mt-2 text-xs" data-testid="ops-po-inbox-paste-body"
                  placeholder={"Subject: Order Acknowledgement - PO 90006-001\n\nSales Order # SO-55123\nEstimated ship date: 11/20/2026"} />
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={busy} onClick={go} data-testid="ops-po-inbox-paste-go">{busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Read it</Button>
        </div>
      </div>
    </div>
  );
}
